import classNames from 'classnames'
import { Link } from 'gatsby'
import React, { useContext, useRef } from 'react'

import { useSelectedCurrency } from '../../hooks/useSelectedCurrency'
import { formatPriceRange, LocaleContext } from '../../locale/i18n'
import { staticPagePaths } from '../../locale/pathing'
import { Product } from '../../logic/catalog'
import { normalizeFormula, previewString } from '../../logic/formatting'
import { gaEvents } from '../../logic/ga4'
import { getPriceRange } from '../../logic/price'
import { ProductFiltersByType } from '../../logic/productFiltering'
import { standardToString } from '../../logic/standard'
import { StaticPage } from '../../logic/staticPage'
import { HighlightText } from '../HighlightText'
import IconCart from '../icons/IconCart'

import './ProductListItem.scss'

const ProductListItem = ({
  filters,
  product,
  preview,
}: {
  filters?: ProductFiltersByType
  product: Product
  preview?: boolean
}) => {
  const { locale, t } = useContext(LocaleContext)
  const { currency } = useSelectedCurrency()
  const { slug, standards, descriptionRaw } = product

  const name = normalizeFormula(product.name ?? '')
  const productUrl = [
    staticPagePaths[StaticPage.PRODUCTS][locale],
    slug,
    '/',
  ].join('')
  const priceRange = getPriceRange(product, currency)

  const standardList =
    standards.slice(0, 3).map(standardToString).join(', ') +
    (standards.length > 3 ? ', …' : '')

  const detailsEl = useRef<HTMLDivElement>(null)

  return (
    <Link
      className={classNames('productListItem ga-productPreviewButton', {
        'productListItem--preview': preview,
      })}
      to={productUrl}
      onClick={() => gaEvents.productClicked(product)}
    >
      <div className="productListItem__details" ref={detailsEl}>
        <h4
          className={classNames('productListItem__title', {
            'productListItem__title--preview': preview,
          })}
        >
          <HighlightText highlight={filters?.text.value.split(' ')}>
            {name}
          </HighlightText>
        </h4>
        {standardList && (
          <div className="productListItem__standard">
            <HighlightText highlight={filters?.text.value.split(' ')}>
              {standardList}
            </HighlightText>
          </div>
        )}
        {!preview && descriptionRaw && (
          <div className="productListItem__description">
            <HighlightText highlight={filters?.text.value.split(' ')}>
              {previewString(
                descriptionRaw,
                Math.round(
                  // rough estimation based on font-sizes and spacings
                  300 - standardList.length * 2 - name.length * 1.5,
                ),
              )}
            </HighlightText>
          </div>
        )}
        <div className="spacer" />
      </div>
      <div className="productListItem__footer">
        {product.webshopEnabled && (
          <div className="productListItem__checkoutAvailable">
            <IconCart />
            {t('productListing.selfCheckoutAvailable')}
          </div>
        )}
        <div className="productListItem__pricing">
          {priceRange && (
            <div className="productListItem__price">
              {formatPriceRange(priceRange, locale, currency)}
            </div>
          )}
          <div className="spacer" />
          <span className="productListItem__cta">
            {t('productListing.readMore')}
          </span>
        </div>
      </div>
    </Link>
  )
}
export default ProductListItem
